import type { LayoutDocument } from "../layout/types.js";
import type { RenderNode } from "../layout-compiler/types.js";
import { customDisciplineLabel } from "../custom-disciplines/labels.js";
import {
  compatibleCompositionDesign,
  isCompositionStatusIndicator,
  compositionNodes,
  compositionStructure,
  presentComposition,
  presentCompositionRuntimeColors,
  validCompositionDesign,
  type CompositionPatch,
  type DesignedDocument,
} from "./catalogue-variants.js";
import type { InterfaceDesign } from "./visual-variants.js";
import type { InterfaceColorMode } from "./tokens.js";

export type CompositionAppearance = {
  document: LayoutDocument;
  color: InterfaceColorMode;
  patch: CompositionPatch;
};
const cached = new WeakMap<
  LayoutDocument,
  Map<string, CompositionAppearance | undefined>
>();
/** Resolve on the immutable source, never on a materialized preview. Localized labels are not structural edits. */
export function readCompositionAppearance(
  document: LayoutDocument | null | undefined,
  mode: InterfaceDesign,
  color: InterfaceColorMode,
  labels?: Record<string, string>,
): CompositionAppearance | undefined {
  if (!document || mode !== "modern") return undefined;
  let cache = cached.get(document);
  const cacheKey = color;
  if (cache?.has(cacheKey)) return cache.get(cacheKey);
  const candidate = (document as DesignedDocument).interfaceDesign;
  let valid = compatibleCompositionDesign(document, candidate);
  const directlyCompatible = valid;
  if (!valid && labels && validCompositionDesign(candidate)) {
    try {
      const structure = JSON.parse(candidate.sourceStructure);
      if (Array.isArray(structure)) {
        const localized = structure.map((node: unknown) =>
          Array.isArray(node)
            ? node.map((v, i) =>
                i === 4 && typeof v === "string"
                  ? customDisciplineLabel(labels, v)
                  : v,
              )
            : node,
        );
        valid =
          JSON.stringify(localized) === compositionStructure(document) &&
          compatibleCompositionDesign(document, {
            ...candidate,
            sourceStructure: compositionStructure(document),
          });
      }
    } catch {
      /* A stale or invalid extension falls back to the original. */
    }
  }
  const result = valid
    ? { document, color, patch: candidate![color] }
    : undefined;
  if (!cache) cached.set(document, (cache = new Map()));
  if (directlyCompatible || !validCompositionDesign(candidate))
    cache.set(cacheKey, result);
  return result;
}

/** For grid measurements and CSS samples only. Runtime programs always receive the source. */
export function compositionForMeasurement(
  value: unknown,
  mode: InterfaceDesign = "original",
  color: InterfaceColorMode = "light",
): unknown {
  let doc: DesignedDocument;
  try {
    doc =
      typeof value === "string"
        ? JSON.parse(value)
        : (value as DesignedDocument);
  } catch {
    return value;
  }
  if (!doc?.scene?.children || !doc.meta?.canvas) return value;
  return presentComposition(doc, mode, color);
}
export function cellForMeasurement(
  value: unknown,
  mode: InterfaceDesign,
  color: InterfaceColorMode,
): unknown {
  let cell: any;
  try {
    cell = typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return value;
  }
  if (!cell || mode === "original") return cell;
  return {
    ...cell,
    layout: compositionForMeasurement(cell.layout, mode, color),
    headerLayout: compositionForMeasurement(cell.headerLayout, mode, color),
    ...(cell.headerGroup
      ? {
          headerGroup: {
            ...cell.headerGroup,
            layout: compositionForMeasurement(
              cell.headerGroup.layout,
              mode,
              color,
            ),
          },
        }
      : {}),
  };
}

const plans = new WeakMap<
  CompositionAppearance,
  Map<
    string,
    { patch: CompositionPatch["nodes"][string]; controlled: Set<string> }
  >
>();
function plan(appearance: CompositionAppearance) {
  const known = plans.get(appearance);
  if (known) return known;
  const nodes = compositionNodes(appearance.document);
  const byId = new Map<
    string,
    { patch: CompositionPatch["nodes"][string]; controlled: Set<string> }
  >();
  const seen = new Set<string>(),
    duplicate = new Set<string>();
  const controlled = (value: unknown, out = new Set<string>()): Set<string> => {
    if (!value || typeof value !== "object") return out;
    const item = value as Record<string, unknown>;
    if (item.type === "action" && typeof item.key === "string")
      out.add(item.key);
    for (const [key, child] of Object.entries(item))
      if (!["tooltip", "interfaceDesign", "children", "props"].includes(key))
        controlled(child, out);
    return out;
  };
  const sceneControlled = controlled(appearance.document.scene.catalog);
  for (const { key, node } of nodes) {
    if (seen.has(node.id)) duplicate.add(node.id);
    seen.add(node.id);
    const patch = appearance.patch.nodes[key];
    if (patch && !isCompositionStatusIndicator(node))
      byId.set(node.id, {
        patch,
        controlled: new Set([...sceneControlled, ...controlled(node.behavior)]),
      });
  }
  // Ambiguous repeated IDs must not receive another template's visual changes.
  for (const id of duplicate) byId.delete(id);
  plans.set(appearance, byId);
  return byId;
}
/** Apply only paint and geometry after evaluation; leave values, effects, hit targets and programs intact. */
export function presentCatalogueRenderNodes(
  nodes: RenderNode[],
  appearance?: CompositionAppearance,
): RenderNode[] {
  if (!appearance) return nodes;
  const patches = plan(appearance);
  const visit = (node: RenderNode): RenderNode => {
    const entry = patches.get(node.id);
    const geometry = entry
      ? Object.fromEntries(
          Object.entries(entry.patch).filter(
            ([key]) => key !== "style" && !entry.controlled.has(key),
          ),
        )
      : {};
    const style = entry?.patch.style
      ? Object.fromEntries(
          Object.entries(entry.patch.style).filter(
            ([key]) => !entry.controlled.has(key),
          ),
        )
      : {};
    return {
      ...node,
      ...geometry,
      style: { ...node.style, ...style },
      ...("children" in node ? { children: node.children.map(visit) } : {}),
    } as RenderNode;
  };
  return presentCompositionRuntimeColors(
    nodes.map(visit),
    appearance.document,
    appearance.color,
  );
}

const object = (v: unknown): v is Record<string, any> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const document = (v: unknown): v is DesignedDocument =>
  object(v) &&
  object(v.meta) &&
  object(v.scene) &&
  Array.isArray(v.scene.children);
/** Keep additive metadata omitted by older clients. Explicit null and explicit replacements always win. */
export function preserveCompositionDesigns<T>(
  incoming: T,
  existing: unknown,
): T {
  if (
    incoming == null ||
    typeof incoming !== "object" ||
    existing == null ||
    typeof existing !== "object"
  )
    return incoming;
  if (Array.isArray(incoming)) {
    if (!Array.isArray(existing)) return incoming;
    return incoming.map((item, index) => {
      const previous =
        object(item) && (item.id != null || item.key != null)
          ? existing.find(
              (old) =>
                object(old) &&
                ((item.id != null && old.id === item.id) ||
                  (item.key != null &&
                    old.key === item.key &&
                    old.scope === item.scope)),
            )
          : existing[index];
      return preserveCompositionDesigns(item, previous);
    }) as T;
  }
  if (!object(incoming) || !object(existing)) return incoming;
  const result: Record<string, unknown> = {
    ...(incoming as Record<string, unknown>),
  };
  if (
    document(incoming) &&
    document(existing) &&
    !Object.prototype.hasOwnProperty.call(incoming, "interfaceDesign") &&
    Object.prototype.hasOwnProperty.call(existing, "interfaceDesign")
  )
    result.interfaceDesign = existing.interfaceDesign;
  for (const [key, child] of Object.entries(incoming))
    if (
      ![
        "behavior",
        "graph",
        "catalog",
        "interfaceDesign",
        "designVariants",
      ].includes(key)
    )
      result[key] = preserveCompositionDesigns(
        child,
        (existing as Record<string, unknown>)[key],
      );
  return result as T;
}
/** Validate extensions at every nested document boundary without changing legacy layout validation. */
export function validCompositionMetadataTree(value: unknown): boolean {
  if (!value || typeof value !== "object") return true;
  if (
    document(value) &&
    value.interfaceDesign != null &&
    !validCompositionDesign(value.interfaceDesign)
  )
    return false;
  return Object.entries(value).every(
    ([key, child]) =>
      [
        "behavior",
        "graph",
        "catalog",
        "interfaceDesign",
        "designVariants",
      ].includes(key) || validCompositionMetadataTree(child),
  );
}
